/**
 * Address Autocomplete — powered by Radar.io
 * Type-ahead input that auto-fills address, city, and state fields.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import Radar from 'radar-sdk-js';
import type { RadarAutocompleteAddress } from 'radar-sdk-js';

const RADAR_KEY = import.meta.env.VITE_RADAR_PUBLISHABLE_KEY as string | undefined;

// Initialize once
let radarInitialized = false;
function ensureRadar() {
  if (radarInitialized || !RADAR_KEY) return;
  Radar.initialize(RADAR_KEY, { logLevel: 'none' });
  radarInitialized = true;
}

export interface AddressResult {
  address: string;   // Street address (e.g. "123 Main St")
  city: string;
  state: string;     // State code (e.g. "TX")
  postalCode: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSelect: (result: AddressResult) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

export function AddressAutocomplete({ value, onChange, onSelect, placeholder, style }: Props) {
  const [suggestions, setSuggestions] = useState<RadarAutocompleteAddress[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  // If no Radar key, render a plain input
  if (!RADAR_KEY) {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Address'}
        style={style}
      />
    );
  }

  ensureRadar();

  // ───── Click outside to close ─────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ───── Debounced search ─────
  const search = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { addresses } = await Radar.autocomplete({
          query,
          layers: ['address'],
          countryCode: 'US',
          limit: 5,
        });
        setSuggestions(addresses);
        setOpen(addresses.length > 0);
        setActiveIndex(-1);
      } catch (err) {
        console.error('[AddressAutocomplete] Search failed:', err);
        setSuggestions([]);
      }
    }, 300);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    search(val);
  };

  const handleSelect = (addr: RadarAutocompleteAddress) => {
    const result: AddressResult = {
      address: addr.addressLabel || '',
      city: addr.city || '',
      state: addr.stateCode || addr.state || '',
      postalCode: addr.postalCode || '',
    };
    onChange(result.address);
    onSelect(result);
    setOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        placeholder={placeholder || 'Start typing an address...'}
        style={style}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div style={dropdownStyles.container}>
          {suggestions.map((addr, i) => (
            <div
              key={`${addr.formattedAddress}-${i}`}
              onClick={() => handleSelect(addr)}
              onMouseEnter={() => setActiveIndex(i)}
              style={{
                ...dropdownStyles.item,
                background: i === activeIndex ? '#262626' : 'transparent',
              }}
            >
              <div style={dropdownStyles.primary}>{addr.addressLabel}</div>
              <div style={dropdownStyles.secondary}>
                {[addr.city, addr.stateCode, addr.postalCode].filter(Boolean).join(', ')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const dropdownStyles: Record<string, React.CSSProperties> = {
  container: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: 8,
    zIndex: 200,
    boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
    overflow: 'hidden',
  },
  item: {
    padding: '10px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #262626',
  },
  primary: {
    color: '#f1f5f9',
    fontSize: 13,
    fontWeight: 500,
  },
  secondary: {
    color: '#64748b',
    fontSize: 11,
    marginTop: 2,
  },
};
